import { DataGridColumn } from 'components/Sov/components/SovEditGrid/components/DataGrid';
import * as Yup from 'yup';

import { highPrecisionNegativeNumberMask, numberMask, yearMask, zipCodeMask } from 'helpers/masks';
import { LocationOptionsData } from 'types/types.type';
import { STATE_LIST, YES_NO_OPTIONS } from '../../../../constants';
import { PropertyColumn } from '../../../../types/submission.type';
import { suppressUpDownEnterForDropDowns } from '../../../../helpers/sov';
import { allowTypeOrBlank } from '../../../../helpers/typeHelper';
import { TextInput } from '../../../../components/Input';
import SoftCostLineItemsInputForm from './Components/SoftCostLineItemsInputForm';
import React from 'react';
import { ValueGetterParams } from 'ag-grid-enterprise';
import { getBrTcv } from 'helpers/brCalculationHelper';
import moment from 'moment';

const columnDetails = (locationOptions: LocationOptionsData): Record<string, DataGridColumn> => ({
    REMOVE_ICON: {
        columnName: '',
        key: 'markedAsDeleted',
        inputType: 'icon',
        required: false,
        columnDef: {
            width: 50,
            headerName: '',
            field: 'markedAsDeleted',
            pinned: 'left',
            lockPinned: true,
        },
    },
    LOCATION_NAME: {
        columnName: 'Location Name',
        key: 'locationName',
        required: true,
        validation: Yup.string().required('Required').nullable(false),
        columnDef: {
            headerName: 'Location Name',
            field: 'locationName',
            width: 400,
            pinned: 'left',
            lockPinned: true,
        },
    },
    LOCATION_ADDRESS: {
        columnName: 'Location Address',
        key: 'address1',
        required: true,
        validationSort: [
            ['address1', 'city'],
            ['address1', 'state'],
            ['address1', 'zip'],
            ['address1', 'locationLat'],
            ['address1', 'locationLon'],
        ],
        validation: Yup.string()
            .nullable()
            .when(
                ['city', 'state', 'zip', 'locationLat', 'locationLon'],
                (
                    city: string,
                    state: string,
                    zip: string,
                    locationLat: number,
                    locationLon: number,
                    schema: Yup.MixedSchema
                ) => {
                    return schema.test({
                        test: (value?: string) => !!value || (!!locationLat && !!locationLon),
                        message: 'must provide a valid physical address or a lat/long',
                    });
                }
            ),
        columnDef: {
            headerName: 'Location Address',
            field: 'address1',
            width: 400,
        },
    },
    LOCATION_ADDRESS_2: {
        columnName: 'Location Address 2',
        key: 'address2',
        required: false,
        columnDef: {
            headerName: 'Location Address 2',
            field: 'address2',
            width: 175,
        },
    },
    CITY: {
        columnName: 'City',
        key: 'city',
        required: true,
        validationSort: [
            ['city', 'address1'],
            ['city', 'state'],
            ['city', 'zip'],
            ['city', 'locationLat'],
            ['city', 'locationLon'],
        ],
        validation: Yup.string()
            .nullable()
            .when(
                ['address1', 'state', 'zip', 'locationLat', 'locationLon'],
                (
                    address1: string,
                    state: string,
                    zip: string,
                    locationLat: number,
                    locationLon: number,
                    schema: Yup.MixedSchema
                ) => {
                    return schema.test({
                        test: (value?: string) => !!value || (!!locationLat && !!locationLon),
                        message: 'must provide a valid physical address or a lat/long',
                    });
                }
            ),
        columnDef: {
            headerName: 'City',
            field: 'city',
            width: 150,
        },
    },
    STATE: {
        columnName: 'State',
        key: 'state',
        required: true,
        inputType: 'drop-down',
        optionsList: STATE_LIST,
        validation: Yup.string()
            .nullable()
            .oneOf([...STATE_LIST, null])
            .when(
                ['address1', 'city', 'zip', 'locationLat', 'locationLon'],
                (
                    address1: string,
                    city: string,
                    zip: string,
                    locationLat: number,
                    locationLon: number,
                    schema: Yup.MixedSchema
                ) => {
                    return schema.test({
                        test: (value?: string) => !!value || (!!locationLat && !!locationLon),
                        message: 'must provide a valid physical address or a lat/long',
                    });
                }
            ),
        columnDef: {
            headerName: 'State',
            field: 'state',
            width: 130,
            suppressKeyboardEvent: suppressUpDownEnterForDropDowns,
        },
    },
    ZIP: {
        columnName: 'Zip',
        key: 'zip',
        required: true,
        mask: zipCodeMask,
        validationSort: [
            ['zip', 'address1'],
            ['zip', 'city'],
            ['zip', 'state'],
            ['zip', 'locationLat'],
            ['zip', 'locationLon'],
        ],
        validation: Yup.number()
            .typeError('Please enter a number')
            .min(5)
            .nullable()
            .when(
                ['address1', 'city', 'state', 'locationLat', 'locationLon'],
                (
                    address1: string,
                    city: string,
                    state: string,
                    locationLat: number,
                    locationLon: number,
                    schema: Yup.MixedSchema
                ) => {
                    return schema.test({
                        test: (value?: string) => !!value || (!!locationLat && !!locationLon),
                        message: 'must provide a valid physical address or a lat/long',
                    });
                }
            ),
        columnDef: {
            headerName: 'Zip',
            field: 'zip',
            width: 115,
        },
    },
    COUNTY: {
        columnName: 'County',
        key: 'county',
        required: false,
        validation: Yup.string().max(128, 'Please limit to 128 characters').nullable(true),
        columnDef: {
            headerName: 'County',
            field: 'county',
            width: 150,
        },
    },
    LATITUDE: {
        columnName: 'Latitude',
        key: 'locationLat',
        required: true,
        mask: highPrecisionNegativeNumberMask,
        validationSort: [
            ['locationLat', 'address1'],
            ['locationLat', 'city'],
            ['locationLat', 'state'],
            ['locationLat', 'zip'],
            ['locationLat', 'locationLon'],
        ],
        validation: Yup.number()
            .typeError('Please enter a number')
            .transform((_, n) => (n ? parseLocaleNumber(n) : null))
            .min(-90, 'Must be between -90 and 90')
            .max(90, 'Must be between -90 and 90')
            .nullable()
            .when(
                ['address1', 'city', 'state', 'zip', 'locationLon'],
                (
                    address1: string,
                    city: string,
                    state: string,
                    zip: string,
                    locationLon: number,
                    schema: Yup.MixedSchema
                ) => {
                    return schema.test({
                        test: (value?: string) => (!!address1 && !!city && !!state && !!zip) || !!value,
                        message: 'must provide a valid physical address or a lat/long',
                    });
                }
            ),
        columnDef: {
            headerName: 'Latitude',
            field: 'lat',
            width: 130,
        },
    },
    LONGITUDE: {
        columnName: 'Longitude',
        key: 'locationLon',
        required: true,
        mask: highPrecisionNegativeNumberMask,
        validationSort: [
            ['locationLon', 'address1'],
            ['locationLon', 'city'],
            ['locationLon', 'state'],
            ['locationLon', 'zip'],
            ['locationLon', 'locationLat'],
        ],
        validation: Yup.number()
            .typeError('Please enter a number')
            .transform((_, n) => (n ? parseLocaleNumber(n) : null))
            .min(-180, 'Must be between -180 and 180')
            .max(180, 'Must be between -180 and 180')
            .nullable()
            .when(
                ['address1', 'city', 'state', 'zip', 'locationLat'],
                (
                    address1: string,
                    city: string,
                    state: string,
                    zip: string,
                    locationLat: number,
                    schema: Yup.MixedSchema
                ) => {
                    return schema.test({
                        test: (value?: string) => (!!address1 && !!city && !!state && !!zip) || !!value,
                        message: 'must provide a valid physical address or a lat/long',
                    });
                }
            ),
        columnDef: {
            headerName: 'Longitude',
            field: 'long',
            width: 130,
        },
    },
    YEAR_OF_CONSTRUCTION: {
        columnName: 'Year of Construction',
        key: 'yearOfConstruction',
        required: false,
        mask: yearMask,
        validation: Yup.number()
            .min(1900, 'Enter a year after 1900')
            .max(moment().add(2, 'months').year(), 'Enter a year before this year')
            .nullable(true),
        columnDef: {
            headerName: 'Year of Construction',
            field: 'yearOfConstruction',
            width: 140,
        },
    },
    CONSTRUCTION_TYPE: {
        columnName: 'Construction Type',
        key: 'constructionType',
        width: 250,
        required: true,
        inputType: 'drop-down',
        options: locationOptions.LOCATION_CONSTRUCTION_TYPES,
        validation: Yup.string()
            .required('Required')
            .oneOf(Object.keys(locationOptions.LOCATION_CONSTRUCTION_TYPES), 'Must be one of the drop down values'),
        columnDef: {
            headerName: 'Construction Type',
            field: 'constructionType',
            width: 250,
            suppressKeyboardEvent: suppressUpDownEnterForDropDowns,
        },
    },
    NUMBER_OF_BUILDINGS: {
        columnName: '#Bldings',
        key: 'numberOfBuildings',
        required: true,
        mask: numberMask,
        validation: Yup.number().typeError('Please enter a number').required('Required').min(1).nullable(true),
        columnDef: {
            headerName: '#Bldings',
            field: 'numberOfBuildings',
            width: 120,
        },
    },
    NUMBER_OF_STORIES: {
        columnName: '# Stories',
        key: 'numberOfStories',
        required: false,
        mask: numberMask,
        validation: Yup.number().typeError('Please enter a number').min(1).nullable(true),
        columnDef: {
            headerName: '# Stories',
            field: 'numberOfStories',
            width: 120,
        },
    },
    MINIMUM_SPACE_SEPARATION_FEET: {
        columnName: 'Min Separation (ft)',
        key: 'minimumSpaceSeparationFeet',
        required: false,
        mask: numberMask,
        validation: Yup.number()
            .typeError('Please enter a number')
            .transform((_, n) => (n ? parseLocaleNumber(n) : null))
            .min(0, 'Must be zero or positive')
            .nullable(true),
        columnDef: {
            headerName: 'Min Space Separation (ft)',
            field: 'minimumSpaceSeparationFeet',
            width: 160,
        },
    },
    IS_FENCED: {
        columnName: 'Fenced',
        key: 'isFencedType',
        required: false,
        inputType: 'drop-down',
        options: locationOptions.LOCATION_IS_FENCED_TYPES,
        validation: Yup.string()
            .nullable(true)
            .oneOf(allowTypeOrBlank(locationOptions.LOCATION_IS_FENCED_TYPES), 'Must be one of the drop down values'),
        columnDef: {
            headerName: 'Fenced',
            field: 'isFencedType',
            width: 160,
            suppressKeyboardEvent: suppressUpDownEnterForDropDowns,
        },
    },
    FENCE_HEIGHT: {
        columnName: 'Fence Height',
        key: 'fenceHeightType',
        required: false,
        inputType: 'drop-down',
        options: locationOptions.LOCATION_FENCE_HEIGHT_TYPES,
        validation: Yup.string()
            .oneOf(allowTypeOrBlank(locationOptions.LOCATION_FENCE_HEIGHT_TYPES), 'Must be one of the drop down values')
            .nullable(true),
        columnDef: {
            headerName: 'Fence Height',
            field: 'fenceHeightType',
            width: 180,
            suppressKeyboardEvent: suppressUpDownEnterForDropDowns,
        },
    },
    IS_GUARDED: {
        columnName: 'Guard',
        key: 'isGuarded',
        required: false,
        inputType: 'drop-down',
        options: YES_NO_OPTIONS,
        validation: Yup.string().oneOf(allowTypeOrBlank(YES_NO_OPTIONS)).nullable(true),
        columnDef: {
            headerName: 'Guard',
            field: 'isGuarded',
            width: 140,
            suppressKeyboardEvent: suppressUpDownEnterForDropDowns,
        },
    },
    HAS_ELECTRONIC_INTRUSION_DETECTION: {
        columnName: 'Elec. Intrusion Det.',
        key: 'hasElectronicIntrusionDetection',
        required: false,
        inputType: 'drop-down',
        options: YES_NO_OPTIONS,
        validation: Yup.string().oneOf(allowTypeOrBlank(YES_NO_OPTIONS)).nullable(true),
        columnDef: {
            headerName: 'Elec. Intrusion Det.',
            field: 'hasElectronicIntrusionDetection',
            width: 180,
            suppressKeyboardEvent: suppressUpDownEnterForDropDowns,
        },
    },
    ELECTRONIC_INTRUSION_DETECTION_VENDOR_NAME: {
        columnName: 'Elec. Intrusion Det. Vendor',
        key: 'electronicIntrusionDetectionVendorName',
        required: false,
        validation: Yup.string().max(128, 'Please limit to 128 characters').nullable(true),
        columnDef: {
            headerName: 'Elec. Intrusion Det. Vendor',
            field: 'electronicIntrusionDetectionVendorName',
            width: 200,
        },
    },
    IS_ELECTRONIC_INTRUSION_DETECTION_MONITORED: {
        columnName: 'Elec. Intrusion Central Station',
        key: 'isElectronicIntrusionDetectionMonitored',
        required: false,
        inputType: 'drop-down',
        options: YES_NO_OPTIONS,
        validation: Yup.string().oneOf(allowTypeOrBlank(YES_NO_OPTIONS)).nullable(true),
        columnDef: {
            headerName: 'Elec. Intrusion Central Station',
            field: 'isElectronicIntrusionDetectionMonitored',
            width: 180,
            suppressKeyboardEvent: suppressUpDownEnterForDropDowns,
        },
    },
    HAS_WATER_FLOW_DETECTION: {
        columnName: 'Water Flow Det.',
        key: 'hasWaterFlowDetection',
        required: false,
        inputType: 'drop-down',
        options: YES_NO_OPTIONS,
        validation: Yup.string().oneOf(allowTypeOrBlank(YES_NO_OPTIONS)).nullable(true),
        columnDef: {
            headerName: 'Water Flow Det.',
            field: 'hasWaterFlowDetection',
            width: 180,
            suppressKeyboardEvent: suppressUpDownEnterForDropDowns,
        },
    },
    IS_WATER_FLOW_CENTRAL_STATION_MONITORED: {
        columnName: 'Water Flow Central Station',
        key: 'isWaterFlowCentralStationMonitored',
        required: false,
        inputType: 'drop-down',
        options: YES_NO_OPTIONS,
        validation: Yup.string().oneOf(allowTypeOrBlank(YES_NO_OPTIONS)).nullable(true),
        columnDef: {
            headerName: 'Water Flow Central Station',
            field: 'isWaterFlowCentralStationMonitored',
            width: 180,
            suppressKeyboardEvent: suppressUpDownEnterForDropDowns,
        },
    },
    HAS_WATER_FLOW_AUTO_REMOTE_SHUT_OFF: {
        columnName: 'Water Flow Auto Shut Off',
        key: 'hasWaterFlowAutoRemoteShutOff',
        required: false,
        inputType: 'drop-down',
        options: YES_NO_OPTIONS,
        validation: Yup.string().oneOf(allowTypeOrBlank(YES_NO_OPTIONS)).nullable(true),
        columnDef: {
            headerName: 'Water Flow Auto Shut Off',
            field: 'hasWaterFlowAutoRemoteShutOff',
            width: 180,
            suppressKeyboardEvent: suppressUpDownEnterForDropDowns,
        },
    },
    HAS_EXTERNAL_COMBUSTIBLE_EXPOSURES_LT_100FT: {
        columnName: 'Ext. Comb. Exposure < 100ft',
        key: 'hasExternalCombustibleExposuresLt100ft',
        required: false,
        inputType: 'drop-down',
        options: YES_NO_OPTIONS,
        validation: Yup.string().oneOf(allowTypeOrBlank(YES_NO_OPTIONS)).nullable(true),
        columnDef: {
            headerName: 'Ext. Comb. Exposure < 100ft',
            field: 'hasExternalCombustibleExposuresLt100ft',
            width: 180,
            suppressKeyboardEvent: suppressUpDownEnterForDropDowns,
        },
    },
    HARD_COSTS: {
        columnName: 'Hard Costs',
        key: 'hardCosts',
        required: false,
        mask: numberMask,
        inputType: 'currency',
        validation: Yup.number()
            .typeError('Please enter a number')
            .transform((_, n) => (n ? parseLocaleNumber(n) : null))
            .min(0, 'Must be zero or positive')
            .nullable(true),
        columnDef: {
            headerName: 'Hard Costs',
            field: 'hardCosts',
            width: 180,
        },
    },
    SOFT_COSTS: {
        columnName: 'Soft Costs',
        key: 'softCosts',
        required: false,
        mask: numberMask,
        inputType: 'currency',
        validation: Yup.number()
            .typeError('Please enter a number')
            .transform((_, n) => (n ? parseLocaleNumber(n) : null))
            .min(0, 'Must be zero or positive')
            .nullable(true),
        columnDef: {
            headerName: 'Soft Costs',
            field: 'softCosts',
            width: 180,
        },
    },
    SOFT_COST_LINE_ITEMS: {
        columnName: 'Soft Cost Line Items',
        key: 'locationSoftCosts',
        inputType: 'form',
        required: false,
        inputForm: SoftCostLineItemsInputForm,
        columnDef: {
            headerName: 'Soft Cost Line Items',
            field: 'locationSoftCosts',
            width: 180,
        },
    },
    LOSS_OF_GROSS_EARNINGS: {
        columnName: 'Loss of Gross Earnings',
        key: 'lossOfGrossEarnings',
        required: false,
        mask: numberMask,
        inputType: 'currency',
        validation: Yup.number()
            .typeError('Please enter a number')
            .transform((_, n) => (n ? parseLocaleNumber(n) : null))
            .min(0, 'Must be zero or positive')
            .nullable(true),
        columnDef: {
            headerName: 'Loss of Gross Earnings',
            field: 'lossOfGrossEarnings',
            width: 180,
        },
    },
    LOSS_OF_RENTAL_INCOME: {
        columnName: 'Loss of Rental Income',
        key: 'lossOfRentalIncome',
        required: false,
        mask: numberMask,
        inputType: 'currency',
        validation: Yup.number()
            .typeError('Please enter a number')
            .transform((_, n) => (n ? parseLocaleNumber(n) : null))
            .min(0, 'Must be zero or positive')
            .nullable(true),
        columnDef: {
            headerName: 'Loss of Rental Income',
            field: 'lossOfRentalIncome',
            width: 170,
        },
    },
    TOTAL_INSURABLE_VALUE: {
        columnName: 'Total Contract Value',
        key: 'tiv',
        valueGetter: (data) => {
            const matchHardCosts = (data?.hardCosts?.toString() || '0').match(/[0-9,.]+/)[0];
            const matchSoftCosts = (data?.softCosts?.toString() || '0').match(/[0-9,.]+/)[0];
            const matchLossOfGrossEarnings = (data?.lossOfGrossEarnings?.toString() || '0').match(/[0-9,.]+/)[0];
            const matchLossOfRentalIncome = (data?.lossOfRentalIncome?.toString() || '0').match(/[0-9,.]+/)[0];
            return getBrTcv(
                parseLocaleNumber(matchHardCosts || '0'),
                parseLocaleNumber(matchSoftCosts || '0'),
                parseLocaleNumber(matchLossOfGrossEarnings || '0'),
                parseLocaleNumber(matchLossOfRentalIncome || '0')
            );
        },
        disabled: true,
        required: false,
        inputType: 'currency',
        mask: numberMask,
        columnDef: {
            headerName: 'TCV',
            width: 160,
            valueGetter: (params: ValueGetterParams) => {
                const data = params.data;
                const matchHardCosts = (data?.hardCosts?.toString() || '0').match(/[0-9,.]+/)[0];
                const matchSoftCosts = (data?.softCosts?.toString() || '0').match(/[0-9,.]+/)[0];
                const matchLossOfGrossEarnings = (data?.lossOfGrossEarnings?.toString() || '0').match(/[0-9,.]+/)[0];
                const matchLossOfRentalIncome = (data?.lossOfRentalIncome?.toString() || '0').match(/[0-9,.]+/)[0];
                return getBrTcv(
                    parseLocaleNumber(matchHardCosts || '0'),
                    parseLocaleNumber(matchSoftCosts || '0'),
                    parseLocaleNumber(matchLossOfGrossEarnings || '0'),
                    parseLocaleNumber(matchLossOfRentalIncome || '0')
                );
            },
        },
    },
});

function parseLocaleNumber(str: string | number) {
    // Detect the user's locale decimal separator:
    const decimalSeparator = (1.1).toLocaleString().substring(1, 2);
    // Detect the user's locale thousand separator:
    let thousandSeparator = (1000).toLocaleString().substring(1, 2);
    // In case there are locales that don't use a thousand separator
    if (thousandSeparator.match(/\d/)) {
        thousandSeparator = '';
    }

    if (typeof str !== 'string') {
        return str;
    }

    let str1 = str as string;
    str1 = str1
        .replace(new RegExp('[' + thousandSeparator + ']', 'g'), '')
        .replace(new RegExp('[' + decimalSeparator + ']'), '.');

    return parseFloat(str1);
}

export function getSOVEditColumns(
    locationOptions: LocationOptionsData,
    disabledColumns?: PropertyColumn[]
): Record<PropertyColumn, DataGridColumn> {
    const ret = columnDetails(locationOptions);

    for (const key in ret) {
        if (ret.hasOwnProperty(key)) {
            const detail = ret[key];
            detail.columnDef.colId = key;
            if (disabledColumns && disabledColumns.indexOf(key as PropertyColumn) >= 0) {
                // this column is disabled
                detail.disabled = true;
            }
        }
    }
    return ret;
}
